/**
 * Demo mode — the shared, login-free surface at demo.zipcheck.my.
 *
 * STANDING RULE (Kris, 2026-10-08): every ZipCheck feature must work on BOTH
 * production (app.zipcheck.my) and the demo (demo.zipcheck.my) with identical
 * behaviour. The demo is the SAME app: same components, same code path, same
 * styling. It differs in exactly two ways:
 *   1. it signs in with the seeded demo lane (`POST /v1/auth/demo-login`)
 *      instead of an emailed OTP, and
 *   2. it is marked by the HOST, not a badge — URL-only, so it resembles
 *      production as closely as possible for venue demos.
 *
 * It is NEVER a fork, a divergent copy, or a stripped-down variant. If a feature
 * renders on one surface and not the other, that is a bug.
 *
 * The API side of the lane already exists and is inert unless
 * `DEMO_MODE === "true"` on the Worker (see docs/fixtures-and-demo.md), so
 * production is unaffected no matter what this module does.
 */

/** The demo host. Must match the Worker's route binding + the CF DNS record. */
export const DEMO_HOST = 'demo.zipcheck.my'

/**
 * The seeded demo accounts (allowlist mirrors the Worker's `DEMO_EMAILS`).
 * These are shared, non-personal simulation accounts — never a real user.
 */
export const DEMO_GUEST_EMAIL = 'guest@example.com'
export const DEMO_STAFF_EMAIL = 'staff@clubmatrix.de'

/** True when the app is being served from the demo host. */
export function isDemoHost(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname.toLowerCase() === DEMO_HOST
}

/**
 * True when this session is the demo lane.
 *
 * Host OR the explicit `?demo=1` flag: the flag lets a demo be opened on any
 * host for a quick click-through, and the host covers the normal case. Both
 * routes set `session_demo` so the state survives a reload (auth.ts clears it
 * on module load, which is deliberate — a demo session must not outlive a
 * restart into a real sign-in).
 */
export function isDemoMode(): boolean {
  if (typeof window === 'undefined') return false
  if (isDemoHost()) return true
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

/** Remember that this tab is a demo session (read + cleared by auth.ts). */
export function markDemoSession(): void {
  try {
    localStorage.setItem('session_demo', '1')
  } catch {
    /* storage blocked — the host still identifies the demo */
  }
}

/**
 * Which demo account the host should sign in.
 *
 * A guest by default: the demo's job is to show the guest journey. `?as=staff`
 * switches to the venue side so both halves of the product are clickable from
 * the same surface.
 */
export function demoAccountFor(
  query: URLSearchParams = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search),
): { email: string; kind: 'guest' | 'staff' } {
  return query.get('as') === 'staff'
    ? { email: DEMO_STAFF_EMAIL, kind: 'staff' }
    : { email: DEMO_GUEST_EMAIL, kind: 'guest' }
}
